import { useEffect, useRef, useState } from 'react'

import type { Timestamp } from '@bufbuild/protobuf/wkt'
import type { UseQueryResult } from '@tanstack/react-query'
import type { DefaultValues, FieldValues } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import {
  type UseCrudModalFormOptions,
  type UseCrudModalFormReturn,
  useCrudModalForm,
} from '~/hooks/useCrudModalForm'

/**
 * フォームを埋めた時点の entity と、その版の更新日時。 保存はこの版を基準に楽観ロックをかける。
 */
export type EditBase<Entity> = {
  entity: Entity
  expectedUpdatedAt: Timestamp
}

type VersionedEntity = { updatedAt?: Timestamp }

declare const entitySourceBrand: unique symbol

/**
 * フォームを埋める元。 `entitySourceOf` だけが作れる (query の結果を取り違えて渡させない)。
 */
export type EntitySource<Entity> = {
  readonly [entitySourceBrand]: true
  readonly entity: Entity | undefined
  readonly isFetchedAfterMount: boolean
  readonly isError: boolean
  readonly enabled: boolean
}

type EntityQuery<Data> = Pick<
  UseQueryResult<Data, unknown>,
  'data' | 'isFetchedAfterMount' | 'isError'
>

/**
 * Get の query の結果から、フォームを埋める元を作る。
 *
 * `enabled: false` の間は埋める対象が無い (新規作成・閲覧中) として扱い、控えを捨てる。
 * 閲覧と編集を切り替える画面は、編集中だけ `enabled` にする。
 */
export function entitySourceOf<Data, Entity>(
  query: EntityQuery<Data>,
  select: (data: Data) => Entity | undefined,
  options: { enabled?: boolean } = {},
): EntitySource<Entity> {
  const enabled = options.enabled ?? true
  return {
    entity:
      enabled && query.data !== undefined ? select(query.data) : undefined,
    isFetchedAfterMount: query.isFetchedAfterMount,
    isError: query.isError,
    enabled,
  } as EntitySource<Entity>
}

/**
 * フォームの読み込み状態。 表示 (読み込み中・入力欄) と保存の可否はこの 1 つの値から決める。
 * - `idle`: 埋める対象が無い (`enabled: false`)
 * - `loading`: 埋める前 (mount した後の取得を待っている)。 入力欄を出さず保存させない
 * - `failed`: 埋める前に取得が失敗した。 入力欄を出さず保存させない
 * - `ready`: 埋めた。 以後の再取得の成否では戻らない (控えた版で保存できる)
 */
export type LoadStatus = 'idle' | 'loading' | 'failed' | 'ready'

type UseEditModalFormOptions<
  Entity extends VersionedEntity,
  Input extends FieldValues,
  Output extends FieldValues,
  Confirmation,
> = Omit<
  UseCrudModalFormOptions<Input, Output, Confirmation>,
  'onSubmit' | 'prepareSubmit' | 'confirmationOf'
> & {
  /** 編集する entity (`entitySourceOf`)。 埋める時機は `useLoadedEntity` と同じ */
  source: EntitySource<Entity>
  toFormValues: (entity: Entity) => DefaultValues<Input>
} & (
    | {
        onSubmit: (values: Output, base: EditBase<Entity>) => Promise<void>
        prepareSubmit?: never
        confirmationOf?: never
      }
    | {
        onSubmit?: never
        prepareSubmit: (
          values: Output,
          base: EditBase<Entity>,
        ) => {
          identity: Uint8Array
          send: (confirmed: boolean) => Promise<unknown>
        }
        confirmationOf: (error: unknown) => Confirmation | undefined
      }
  )

type UseEditModalFormReturn<
  Entity,
  Input extends FieldValues,
  Output extends FieldValues,
  Confirmation,
> = Omit<UseCrudModalFormReturn<Input, Output, Confirmation>, 'reset'> & {
  loadStatus: LoadStatus
  /** フォームを埋めた時点の entity (保存の基準と同じ版)。 埋める前は `undefined` */
  loaded: Entity | undefined
}

/**
 * 取得した entity でフォームを 1 回だけ埋め、埋めた時点の entity を返す。 楽観ロックの基準
 * (`updatedAt`) は返した entity から取る。
 *
 * 埋めるのは、mount した後の取得が届いてから。 前に開いた時の取得結果が cache に残っていると
 * 最初の描画でそれが渡るが、それで埋めると古い版に固定され、保存が必ず競合で弾かれる。
 *
 * 埋めた後の再取得では `load` を呼ばず、返す entity も変えない。 埋め直すと入力中の内容が
 * 消え、基準だけ最新にすると他の利用者の更新を黙って上書きする。 `entity` を `undefined` に
 * 戻すと控えを捨て、次に値が来た時に埋め直す (閲覧と編集を切り替える画面は、編集中だけ
 * entity を渡す)。
 *
 * `useCrudModalForm` を使うフォームは `useEditModalForm` を使う。 これは自前の `useForm`
 * を持つフォーム向け。
 */
export function useLoadedEntity<Entity>(
  { entity, isFetchedAfterMount, isError, enabled }: EntitySource<Entity>,
  load: (entity: Entity) => void,
): { loaded: Entity | undefined; loadStatus: LoadStatus } {
  const [loaded, setLoaded] = useState<{ entity: Entity }>()
  const loadRef = useRef(load)
  loadRef.current = load

  useEffect(() => {
    if (entity === undefined) {
      if (loaded !== undefined) setLoaded(undefined)
      return
    }
    // 失敗した取得も isFetchedAfterMount を立てるので、残っていた古い結果で埋めないよう isError も見る
    if (loaded !== undefined || !isFetchedAfterMount || isError) return
    loadRef.current(entity)
    setLoaded({ entity })
  }, [entity, isFetchedAfterMount, isError, loaded])

  return {
    loaded: loaded?.entity,
    loadStatus: !enabled
      ? 'idle'
      : loaded !== undefined
        ? 'ready'
        : isError
          ? 'failed'
          : 'loading',
  }
}

/**
 * 取得した entity を編集するフォーム。 楽観ロックの基準は、ユーザーが見て編集を始めた版
 * (= フォームを埋めた時点の entity) に固定する。
 *
 * 編集中に再取得で entity が新しくなっても、フォームは埋め直さず基準も動かさない。
 * 埋め直すと入力中の内容が消え、基準だけ最新にすると他の利用者の更新を黙って上書きする。
 * 他の利用者が先に更新していれば保存は `Aborted` で失敗する。
 *
 * フォームを埋める経路をこの hook だけに持たせるため、`reset` は返さない。
 */
export function useEditModalForm<
  Entity extends VersionedEntity,
  Input extends FieldValues,
  Output extends FieldValues = Input,
  Confirmation = never,
>(
  options: UseEditModalFormOptions<Entity, Input, Output, Confirmation>,
): UseEditModalFormReturn<Entity, Input, Output, Confirmation> {
  const {
    source,
    toFormValues,
    onSubmit,
    prepareSubmit,
    confirmationOf,
    ...rest
  } = options
  const { t } = useTranslation()
  // submit は描画の後に呼ばれるので、その時点で埋めた entity を読む
  const loadedRef = useRef<Entity>()

  const requireBase = (): EditBase<Entity> => {
    const loaded = loadedRef.current
    if (loaded?.updatedAt === undefined) {
      // 埋める前・更新日時を持たない entity は保存ボタンを押せないので通常は到達しない
      throw new Error(t(rest.unknownErrorKey ?? 'common:error.occurred'))
    }
    return { entity: loaded, expectedUpdatedAt: loaded.updatedAt }
  }

  const submitOptions: UseCrudModalFormOptions<Input, Output, Confirmation> =
    prepareSubmit !== undefined
      ? {
          ...rest,
          prepareSubmit: (values) => prepareSubmit(values, requireBase()),
          confirmationOf,
        }
      : {
          ...rest,
          onSubmit: (values) => onSubmit(values, requireBase()),
        }
  const { reset, ...form } = useCrudModalForm<Input, Output, Confirmation>(
    submitOptions,
  )

  const { loaded, loadStatus } = useLoadedEntity(source, (e) =>
    reset(toFormValues(e)),
  )
  loadedRef.current = loaded

  return { ...form, loadStatus, loaded }
}
